import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import { Button } from 'primereact/button';
import { getAll as getAllOrganizations } from 'apis/organizations.api';
import {
  getUnitPreQualificationDeadlines,
  setUnitPreQualificationDeadline,
} from 'apis/programs.api';
import { TOAST } from 'constant';
import { toast } from 'layout';

import { mapError } from 'utils/func';
import DeadlineItem from './DeadlineItem';

function DeadlineTableDialog({ visible, setVisible, program }) {
  const { t } = useTranslation();
  const [deadlines, setDeadlines] = useState([]);
  const [isVisibleCloseDialog, setIsVisibleCloseDialog] = useState(false);
  const [isVisibleDeleteDialog, setIsVisibleDeleteDialog] = useState(false);
  const [selectedDeadline, setSelectedDeadline] = useState(undefined);
  const {
    data: unitPreQualificationDeadlinesData,
    isLoading,
    refetch,
  } = useQuery(
    ['unitPreQualificationDeadlines', program.id],
    () => getUnitPreQualificationDeadlines(program.id),
    { enabled: !!program.id }
  );
  const { mutateAsync } = useMutation(setUnitPreQualificationDeadline);
  const { data: organizationsData } = useQuery(['organizations'], () => getAllOrganizations());

  const organizations = useMemo(
    () =>
      organizationsData?.data?.map((organization) => ({
        id: organization?.id,
        name: organization?.name,
      })) || [],
    [organizationsData]
  );

  const unitPreQualificationDeadlines = useMemo(
    () =>
      (unitPreQualificationDeadlinesData?.data?.data ?? []).map((item) => ({
        ...item,
        saved: true,
      })),
    [unitPreQualificationDeadlinesData]
  );

  const handleChangeDeadline = (position, organizationId, deadline) => {
    setDeadlines((prev) => {
      const newDeadlines = prev.slice();
      newDeadlines[position] = {
        deadline,
        organizationId,
        organization: {
          name: organizations.find((item) => item.id === organizationId)?.name,
        },
        saved: true,
        changed: false,
      };

      return [...newDeadlines];
    });
  };

  const handleChangeSavedDeadline = (position, saved) => {
    setDeadlines((prev) => {
      const newDeadlines = prev.slice();
      newDeadlines[position].saved = saved;
      return [...newDeadlines];
    });
  };

  const handleRemoveDeadline = async (position, data) => {
    const { organizationId, saved } = data;

    if (saved) {
      if (organizationId) {
        const res = await mutateAsync({
          programId: program.id,
          data: {
            organizationId,
            isDeleted: true,
          },
        });

        if (res.status === 200) {
          toast(TOAST.SUCCESS, t('program.deadline.deleteSuccess'));
          setDeadlines((prev) => prev.filter((item, index) => index !== position));
        } else {
          const error = res.data;
          toast(TOAST.ERROR, mapError(t, error));
        }
      }
    } else {
      setDeadlines((prev) => prev.filter((item, index) => index !== position));
    }
  };

  const handleChangeSelectedDeadline = (data) => {
    setSelectedDeadline(data);
  };

  const handleChangeVisibleDeleteDialog = () => {
    setIsVisibleDeleteDialog((prev) => !prev);
  };

  useEffect(() => {
    setDeadlines(unitPreQualificationDeadlines);
  }, [unitPreQualificationDeadlines]);

  const renderAddButton = () => {
    if (deadlines?.length < organizations?.length) {
      return (
        <div className="flex justify-content-center mt-3 mb-1">
          <Button
            className="py-2 border-none!"
            icon={`pi ${isLoading ? 'pi-spin pi-spinner' : 'pi-plus'}`}
            rounded
            text
            loading={isLoading}
            size="large"
            severity="info"
            aria-label="Add"
            type="button"
            onClick={() => setDeadlines((prev) => [...prev, { saved: false }])}
          />
        </div>
      );
    }

    return null;
  };

  const footerConfirmationClose = () => (
    <div>
      <Button
        label={t('program.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsVisibleCloseDialog(false)}
        autoFocus
      />
      <Button
        label={t('program.ok')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          setVisible(false);
          setIsVisibleCloseDialog(false);
          refetch();
        }}
      />
    </div>
  );

  const footerConfirmationDelete = () => (
    <div>
      <Button
        label={t('program.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsVisibleDeleteDialog(false)}
        autoFocus
      />
      <Button
        label={t('program.ok')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          setIsVisibleDeleteDialog(false);
          handleRemoveDeadline(selectedDeadline.position, selectedDeadline.data);
        }}
      />
    </div>
  );

  return (
    <Dialog
      header={<p className="text-blue-500">{t('program.deadline.title')}</p>}
      visible={visible}
      style={{ width: '800px' }}
      onHide={() => {
        const hasChangedDeadline = deadlines.some((item) => !item.saved);
        if (hasChangedDeadline) {
          setIsVisibleCloseDialog(true);
        } else {
          setVisible(false);
        }
      }}
      draggable={false}
    >
      <p className="">
        {t('program.name')}: <span className="font-bold">{program?.name ?? ''}</span>
      </p>
      <div className="relative">
        <div
          className="p-datatable p-component p-datatable-responsive-scroll p-datatable-scrollable p-datatable-gridlines p-datatable-gridlines"
          data-scrollselectors=".p-datatable-wrapper"
        >
          <div className="p-datatable-wrapper">
            <table className="p-datatable-table">
              <thead className="p-datatable-thead">
                <tr>
                  <th className="w-4rem text-center" style={{ minWidth: '4rem' }}>
                    {t('projectProposal.researchResult.kpis.fields.order')}
                  </th>
                  <th className="w-4rem text-center" style={{ minWidth: '4rem' }}>
                    {t('program.deadline.organization')}
                  </th>
                  <th className="w-4rem text-center" style={{ minWidth: '4rem' }}>
                    {t('program.deadline.name')}
                  </th>
                  <th className="w-4rem text-center" style={{ minWidth: '4rem' }}>
                    {t('program.viewList.action')}
                  </th>
                </tr>
              </thead>
              <tbody className="p-datatable-tbody" key="id">
                {deadlines?.map((item, index) => (
                  <DeadlineItem
                    item={item}
                    position={index}
                    programId={program.id}
                    deadlines={deadlines}
                    organizations={organizations}
                    onChangeDeadline={handleChangeDeadline}
                    onChangeSavedDeadline={handleChangeSavedDeadline}
                    onRemoveDeadline={handleRemoveDeadline}
                    onChangeSelectedDeadline={handleChangeSelectedDeadline}
                    onChangeVisibleDeleteDialog={handleChangeVisibleDeleteDialog}
                    key={`organization-deadline-item-${item.organizationId}-${program.id}`}
                  />
                ))}
              </tbody>
            </table>
            {renderAddButton()}
          </div>
        </div>
      </div>
      <Dialog
        header={<p className="text-red-500">{t('program.dialog.change')}</p>}
        visible={isVisibleCloseDialog}
        position="center"
        style={{ width: '400px' }}
        onHide={() => setIsVisibleCloseDialog(false)}
        draggable={false}
        footer={footerConfirmationClose}
      >
        <p className="text-lg">{t('program.dialog.confirmNotSaveChanges')}</p>
      </Dialog>

      <Dialog
        header={<p className="text-red-500">{t('program.deadline.delete')}</p>}
        visible={isVisibleDeleteDialog}
        position="center"
        style={{ width: '400px' }}
        onHide={() => setIsVisibleDeleteDialog(false)}
        draggable={false}
        footer={footerConfirmationDelete}
      >
        <p className="text-lg">
          {t('program.dialog.confirmDeleteDeadline')}{' '}
          <span className="font-bold">{selectedDeadline?.data?.organization?.name ?? ''}</span>
        </p>
      </Dialog>
    </Dialog>
  );
}

export default DeadlineTableDialog;

DeadlineTableDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  program: PropTypes.object.isRequired,
};
